/**
 * ReadOnlyField Component
 *
 * A read-only field for displaying non-editable values.
 */

import React, { useState } from 'react';
import { Box, Grid, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii } from '../../tokens';

export interface ReadOnlyFieldProps {
    /** Field label */
    label: string;
    /** Display value */
    value: string | number | null | undefined;
    /** Fallback when value is empty */
    fallback?: string;
    /** Grid size (out of 12) */
    size?: number;
    /** Whether to use monospace font */
    mono?: boolean;
    /** Warning message */
    warning?: string;
    /** Whether to show a copy button */
    copyable?: boolean;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({
    label,
    value,
    fallback = 'N/A',
    size = 12,
    mono = false,
    warning,
    copyable = false,
    sx,
}) => {
    const [copied, setCopied] = useState(false);
    const displayValue = value ?? fallback;
    const hasWarning = Boolean(warning);

    const handleCopy = async () => {
        if (value) {
            try {
                await navigator.clipboard.writeText(String(value));
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error('Failed to copy:', err);
            }
        }
    };

    return (
        <Grid item xs={12} sm={12} md={size} sx={sx}>
            <Box>
                <Typography
                    component='label'
                    variant='body2'
                    sx={{
                        display: 'block',
                        mb: '6px',
                        fontWeight: typographyTokens.fontWeight.medium,
                        color: hasWarning ? colors.error.main : colors.neutral[700],
                        fontSize: typographyTokens.fontSize.sm,
                        lineHeight: 1.43,
                    }}>
                    {label}
                </Typography>

                <Box
                    sx={{
                        px: 1.5,
                        pr: copyable ? 0.5 : 1.5,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderRadius: `${radii.md}px`,
                        backgroundColor: colors.neutral[50],
                        border: `1px solid ${hasWarning ? colors.error.main : colors.neutral[200]}`,
                        boxSizing: 'border-box',
                    }}>
                    <Typography
                        variant='body2'
                        sx={{
                            fontFamily: mono ? typographyTokens.fontFamily.mono : typographyTokens.fontFamily.body,
                            fontSize: typographyTokens.fontSize.base,
                            color: value ? colors.neutral[900] : colors.neutral[400],
                            flex: 1,
                        }}>
                        {displayValue}
                    </Typography>
                    
                    {copyable && value && (
                        <Tooltip title={copied ? 'Copied!' : 'Copy'} placement='top'>
                            <IconButton
                                size='small'
                                onClick={handleCopy}
                                sx={{
                                    color: copied ? colors.success.main : colors.neutral[400],
                                    '&:hover': {
                                        color: copied ? colors.success.main : colors.neutral[600],
                                        backgroundColor: colors.neutral[100],
                                    },
                                }}>
                                {copied ? (
                                    <CheckIcon sx={{ fontSize: 16 }} />
                                ) : (
                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {warning && (
                    <Typography
                        variant='caption'
                        sx={{
                            color: colors.error.main,
                            fontSize: typographyTokens.fontSize.xs,
                        }}>
                        {warning}
                    </Typography>
                )}
            </Box>
        </Grid>
    );
};

export default ReadOnlyField;
